// Module 10 — ballistic arc geometry (PURE, unit-tested). The information + finance overlays must NOT
// read as flat edges that mix with the electrical cables, so each is drawn as a SEMI-CURVED BALLISTIC
// ARC that springs off the ground at one node, arches high above the 2D plane, and lands at the other —
// the glowing "global network" look. This module samples that arch: straight lon/lat interpolation
// (metro distances ⇒ great-circle ≈ linear) plus a SINE height profile (0 at both endpoints, apex at
// mid) so the curve starts/ends ON the node and bulges up in between. Apex scales with the chord
// distance so longer links arch higher (finance arches higher than info via a larger apexScale),
// clamped so a 1 km neighbour link still visibly lifts and a cross-GTA link doesn't shoot to space.
// Display-only — Math.sin/cos live in viz, never the deterministic solver. Both the arc LINE
// (overlay-arcs) and the arrow TRACK (flow-glyphs) sample the SAME arch so the arrows ride the line.

export interface ArcSampleOpts {
  segments?: number; // sub-segments; vertices = segments + 1 (default 40 → smooth, no facet kinks)
  apexScale?: number; // apex height = apexScale × chord-metres (default 0.15)
  minApexM?: number; // floor so even short neighbour links visibly arch (default 1200 m)
  maxApexM?: number; // cap so cross-GTA links stay ON-SCREEN at metro zoom (default 9000 m, was 32000 —
  // a 32 km apex spiked off-screen / read as a glitch when zoomed in; ~9 km arches clearly yet stays framed)
}

export interface ArcSamples {
  lonlat: [number, number][]; // per-vertex [lon, lat]
  heights: number[]; // per-vertex elevation (m) — sine arch, 0 at both ends
  apexM: number; // the peak elevation actually used
  chordM: number; // straight-line node distance (m)
}

const DEG_M = 111320; // metres per degree of latitude — ample precision for a DISPLAY arch over the GTA

// Straight-line distance between two lon/lat points, metres (equirectangular — fine at metro scale).
export function chordMeters(from: readonly [number, number], to: readonly [number, number]): number {
  const latMid = (from[1] + to[1]) / 2;
  const dLat = (to[1] - from[1]) * DEG_M;
  const dLon = (to[0] - from[0]) * DEG_M * Math.cos((latMid * Math.PI) / 180);
  return Math.hypot(dLat, dLon);
}

// Sample a ballistic arch between two nodes. Endpoints sit at height 0 (on the node); the apex bulges
// up at the midpoint. Returns parallel arrays the renderer feeds straight to Cartesian3.fromDegrees.
export function ballisticArc(
  from: readonly [number, number],
  to: readonly [number, number],
  opts: ArcSampleOpts = {},
): ArcSamples {
  const segments = Math.max(2, Math.floor(opts.segments ?? 40));
  const apexScale = opts.apexScale ?? 0.15;
  const minApexM = opts.minApexM ?? 1200;
  const maxApexM = opts.maxApexM ?? 9000;
  const chordM = chordMeters(from, to);
  const apexM = Math.min(maxApexM, Math.max(minApexM, apexScale * chordM));
  const lonlat: [number, number][] = [];
  const heights: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    lonlat.push([
      from[0] + (to[0] - from[0]) * t,
      from[1] + (to[1] - from[1]) * t,
    ]);
    heights.push(apexM * Math.sin(Math.PI * t)); // 0 → apex → 0, a smooth rounded arch
  }
  return { lonlat, heights, apexM, chordM };
}
